/**
 * Background ABDP sync job for one asset.
 *
 * For large batches the backend does not run classify re-sync / extract /
 * sync_review inside the HTTP request (1,000 PDFs = 1,000 Process folder
 * fetches, which outlives the ingress timeout and surfaces as "Failed to
 * fetch"). Instead `POST …/va/{action}` answers `{ queued: true }` and records
 * progress in the `ABDP_SYNC_JOB` fact, which Digitize already polls.
 */

export const SYNC_JOB_FACT = 'ABDP_SYNC_JOB';
export const SYNC_JOB_ACTIVE = new Set(['QUEUED', 'RUNNING']);
/** Match backend ``_SYNC_JOB_STALE_SECONDS`` (full folder pulls). */
export const SYNC_JOB_STALE_MS = 6 * 60 * 60 * 1000;
/** Match backend ``_FOLLOWUP_STALE_SECONDS`` (classify heartbeat ~60s). */
export const SYNC_JOB_FOLLOWUP_STALE_MS = 5 * 60 * 1000;

function syncJobStaleMs(job) {
  return job?.follow_up ? SYNC_JOB_FOLLOWUP_STALE_MS : SYNC_JOB_STALE_MS;
}

function syncJobUpdatedAt(job) {
  return Date.parse(job?.updated_at || job?.queued_at || '') || 0;
}

/** Parse the ABDP_SYNC_JOB fact out of an extracted-facts response. */
export function readSyncJob(factsResponse) {
  const items = Array.isArray(factsResponse?.items) ? factsResponse.items : [];
  const fact = items.find((f) => f && (f.fact_key === SYNC_JOB_FACT || f.key === SYNC_JOB_FACT));
  const raw = fact?.fact_value ?? fact?.value;
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function isSyncJobActive(job) {
  if (!job || !SYNC_JOB_ACTIVE.has(String(job.status || '').toUpperCase())) {
    return false;
  }
  const updated = syncJobUpdatedAt(job);
  if (!updated) {
    return false;
  }
  return Date.now() - updated < syncJobStaleMs(job);
}

export function isSyncJobQueuedResponse(result) {
  return Boolean(result && result.queued === true);
}

const ACTION_LABEL = {
  classify: 'Classification re-sync',
  extract: 'Extraction',
  sync_review: 'Review sync',
  validate: 'Validation sync',
};

export function syncJobActionLabel(action) {
  return ACTION_LABEL[String(action || '').toLowerCase()] || String(action || 'Sync');
}

export function syncJobAbandonedMessage(job) {
  const label = syncJobActionLabel(job?.action);
  return `${label} stopped (no progress for several minutes — usually after a deploy). Click Run classification to continue.`;
}

/** Operator-facing copy for the current job state. */
export function syncJobMessage(job) {
  if (!job) return '';
  const label = syncJobActionLabel(job.action);
  const status = String(job.status || '').toUpperCase();
  if (status === 'QUEUED') {
    return `${label} is queued in the background — Digitize refreshes when it finishes.`;
  }
  if (status === 'RUNNING') {
    const total = Number(job.validate_total);
    const synced = Number(job.validate_synced);
    if (Number.isFinite(total) && total > 0 && Number.isFinite(synced) && synced > 0) {
      return `${label} is running in the background… ${Math.min(synced, total)} of ${total} loan folder(s) done.`;
    }
    return `${label} is running in the background (every Process folder is being pulled)…`;
  }
  if (status === 'COMPLETE') {
    return job.message ? String(job.message) : `${label} finished.`;
  }
  if (status === 'FAILED') {
    return `${label} failed${job.error ? `: ${job.error}` : '.'} Run it again to retry.`;
  }
  return '';
}

/** A FAILED job older than this is history, not something to surface on load. */
export const SYNC_JOB_FAILURE_VISIBLE_MS = 24 * 60 * 60 * 1000;

/**
 * What the page should do with a job it finds in the facts when it loads
 * (fresh open, reload, or arriving from another tab) — before this, a
 * background validate/extract was invisible unless this very tab started it.
 *
 * @returns {{ resume: object | null, message: string }}
 *   `resume` is a pollJob descriptor when the job is still active;
 *   `message` is operator copy to show (progress, or a recent failure).
 */
export function syncJobOnLoad(factsResponse, { now = Date.now() } = {}) {
  const job = readSyncJob(factsResponse);
  if (!job) return { resume: null, message: '' };
  if (isSyncJobActive(job)) {
    const startedAt = Date.parse(job.queued_at || job.updated_at || '') || now;
    return {
      resume: { action: job.action, startedAt, label: syncJobActionLabel(job.action) },
      message: syncJobMessage(job),
    };
  }
  const status = String(job.status || '').toUpperCase();
  if (SYNC_JOB_ACTIVE.has(status)) {
    return { resume: null, message: syncJobAbandonedMessage(job) };
  }
  const finishedAt = Date.parse(job.finished_at || job.updated_at || '') || 0;
  const recent = finishedAt && now - finishedAt <= SYNC_JOB_FAILURE_VISIBLE_MS;
  if (status === 'FAILED' && recent) {
    return { resume: null, message: syncJobMessage(job) };
  }
  if (status === 'COMPLETE' && recent && syncJobFolderGap(job) > 0) {
    // Validation finished but ABDP had no rule cards for some folders —
    // "675 rules" must not read as "every loan is done".
    return { resume: null, message: syncJobMessage(job) };
  }
  return { resume: null, message: '' };
}

/** Folders a validate job asked about that came back without rules. */
export function syncJobFolderGap(job) {
  if (!job) return 0;
  const empty = Number(job.validate_folders_empty) || 0;
  const failed = Number(job.validate_folders_failed) || 0;
  return empty + failed;
}

/**
 * Decide what a poll tick should do with the latest facts while a background
 * job may be in flight.
 *
 * @returns {{ done: boolean, failed: boolean, message: string }}
 */
export function syncJobPollOutcome(factsResponse, { startedAt = 0 } = {}) {
  const job = readSyncJob(factsResponse);
  if (!job) return { done: false, failed: false, message: '' };
  const status = String(job.status || '').toUpperCase();
  if (
    SYNC_JOB_ACTIVE.has(status) &&
    !isSyncJobActive(job)
  ) {
    return { done: true, failed: false, message: syncJobAbandonedMessage(job) };
  }
  // Ignore a stale COMPLETE/FAILED from a previous run that predates this poll.
  const finishedAt = Date.parse(job.finished_at || job.updated_at || '') || 0;
  if (!isSyncJobActive(job) && startedAt && finishedAt && finishedAt < startedAt) {
    return { done: false, failed: false, message: '' };
  }
  return {
    done: status === 'COMPLETE' || status === 'FAILED',
    failed: status === 'FAILED',
    message: syncJobMessage(job),
  };
}
